import { useQuery } from '@tanstack/react-query';
import { getClassroomData, getClassroomUsers } from 'api/classroomApi';
import IconButton from 'components/common/IconButton';
import { useUser } from 'context/UserContext';
import React, { useEffect, useState } from 'react';
import { Card, Col, Container, Row } from 'react-bootstrap';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loading from '../components/Loading';
import ClassroomCard from './components/ClassroomCard';
import BonusMalusModal from './modals/BonusMalusModal';
import ManageClassroomModal from './modals/ManageClassroomModal';
import ParticipantsModal from './modals/ParticipantsModal';

const Classroom = () => {
  const user = useUser();
  const navigate = useNavigate();

  const {
    data: classroom,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['classroomData'],
    queryFn: getClassroomData
  });

  const [classroomUsers, setClassroomUsers] = useState([]);
  const { data: classroomUsersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['classroomUsers'],
    queryFn: getClassroomUsers
  });

  useEffect(() => {
    if (classroomUsersData) {
      setClassroomUsers(classroomUsersData);
    }
  }, [classroomUsersData]);

  const [showBonusModal, setShowBonusModal] = useState(false);
  const [showMalusModal, setShowMalusModal] = useState(false);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);

  if (isError) {
    toast.error(error.message);
    return <Navigate to="/offline" />;
  }

  if (isLoading || isLoadingUsers) {
    return <Loading />;
  }

  return (
    <>
      <BonusMalusModal
        showModal={showBonusModal}
        setShowModal={setShowBonusModal}
        type="bonus"
      />
      <BonusMalusModal
        showModal={showMalusModal}
        setShowModal={setShowMalusModal}
        type="malus"
      />
      <ParticipantsModal
        showModal={showParticipantsModal}
        setShowModal={setShowParticipantsModal}
        users={classroomUsers}
      />
      {user.prefect && (
        <ManageClassroomModal
          showModal={showManageModal}
          setShowModal={setShowManageModal}
          classroom={classroom}
          username={user.username}
          users={classroomUsers}
        />
      )}
      <Row className="gx-3">
        <Col md={8} className="mb-3">
          <Card className="h-md-100">
            <Card.Body>
              <h2 className="mb-1">{classroom.name}</h2>
              <h4 className="fs-9 fw-normal text-500">
                Codice Classe: <strong>{classroom.code}</strong>
              </h4>
              <IconButton
                size="sm"
                variant="falcon-success"
                className="mt-2 me-2 mb-1"
                icon="user-plus"
                transform="shrink-3"
                onClick={() => setShowBonusModal(true)}
              >
                Bonus
              </IconButton>
              <IconButton
                size="sm"
                variant="falcon-danger"
                className="mt-2 me-2 mb-1"
                icon="user-minus"
                transform="shrink-3"
                onClick={() => setShowMalusModal(true)}
              >
                Malus
              </IconButton>
              {user.prefect && (
                <>
                  <IconButton
                    size="sm"
                    variant="falcon-default"
                    className="mt-2 me-2 mb-1"
                    icon="users-cog"
                    transform="shrink-3"
                    onClick={() => setShowManageModal(true)}
                  >
                    Gestisci classe
                  </IconButton>
                  <IconButton
                    size="sm"
                    variant="falcon-default"
                    className="mt-2 me-2 mb-1"
                    icon="book"
                    transform="shrink-3"
                    onClick={() => navigate('/guide')}
                  >
                    Guida
                  </IconButton>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-3">
          <Card className="h-md-100">
            <Card.Body className="d-flex align-items-center">
              <Container className="p-0">
                <Row>
                  <Col className="text-center border-end border-200 px-2">
                    <a href="#" onClick={() => setShowParticipantsModal(true)}>
                      <h4 className="mb-0">{classroomUsers.length}</h4>
                      <h6 className="text-600 fs-9 mt-1 mb-0">partecipanti</h6>
                    </a>
                  </Col>
                  <Col className="text-center border-end border-200 px-2">
                    <h4 className="mb-0">{classroom.teachers.length}</h4>
                    <h6 className="text-600 fs-9 mt-1 mb-0">professori</h6>
                  </Col>
                  <Col className="text-center px-2">
                    <h4 className="mb-0">{classroom.students.length}</h4>
                    <h6 className="text-600 fs-9 mt-1 mb-0">studenti</h6>
                  </Col>
                </Row>
              </Container>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <ClassroomCard
        type="T"
        components={classroom.teachers}
        prefect={user.prefect}
      />
      <ClassroomCard
        type="S"
        components={classroom.students}
        prefect={user.prefect}
      />
    </>
  );
};

export default Classroom;
